import { type Observable } from 'rxjs';
import type { Classes } from 'jss';

import {
    type AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    type OnInit,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { filter, take } from 'rxjs/operators';

import { CookieService } from 'ngx-cookie-service';

import { EDisplayConditionType, EEqualityType, EDisplayConditionOperator } from '@common/enums/display-condition.enum';
import { ServerStylesService } from '@common/services/server-styles.service';
import { PlatformService } from '@common/services/platform.service';
import { EScrollEffect } from '@common/enums';
import { isIphone } from '@common/helpers/iphone-check';
import { websiteColorCss } from '@libs/themes/src/variants/website/utils/variables';

import type { ConditionContext } from '@web/types';
import { BlockStructureEntity } from '@web/types';
import { getStyles } from '@web/layout/content/page-content/components/blocks/block-wrapper/block-wrapper.component.styles';
import { BlockShapeDividersComponent } from '@web-builder/components/block-wrapper/block-shape-dividers/block-shape-dividers.component';
import { ImageService } from '@web-builder/core/services/image.service';
import { BlockDisplayConditionService } from '@web-builder/core/services/block-display-condition.service';
import { TransferStateService } from '@web-builder/core/services/transfer-state.service';
import { environment } from '@web-builder-env/environment';

@Component({
    selector: 'web-builder-block-wrapper',
    templateUrl: './block-wrapper.component.html',
    styleUrls: ['./block-wrapper.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, BlockShapeDividersComponent],
})
export class BlockWrapperComponent implements OnInit, AfterViewInit {
    @Input() public block: BlockStructureEntity;
    @Input() public index: number;

    @ViewChild('blockBg') blockBg: ElementRef<HTMLDivElement>;
    @ViewChild('parallax') parallax: ElementRef<HTMLDivElement>;

    public classes: Classes;
    public pinedTopBlockHeight: number = 0;
    public pinedBottomBlockHeight: number = 0;
    public imgHeight: number = 0;
    public backgroundColor: string = 'transparent';
    public isBrowser: boolean = this.platformService.isPlatformBrowser();
    public isServer: boolean = this.platformService.isPlatformServer();

    public EDisplayConditionType = EDisplayConditionType;
    public EEqualityType = EEqualityType;
    public EDisplayConditionOperator = EDisplayConditionOperator;

    constructor(
        private readonly elementRef: ElementRef<HTMLDivElement>,
        private readonly stylesService: ServerStylesService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly platformService: PlatformService,
        private readonly imageService: ImageService,
        private readonly renderer: Renderer2,
        private readonly transferStateService: TransferStateService,
        private readonly cookieService: CookieService,
        private readonly blockDisplayConditionService: BlockDisplayConditionService,
    ) {}

    public ngOnInit(): void {
        const styles = getStyles(this.block, false, environment.fileManagerCdnUrl);
        this.classes = this.stylesService.getStyles(this.block.id, styles);

        this.changeDetectorRef.detectChanges();
    }

    public ngAfterViewInit(): void {
        if (this.platformService.isPlatformServer()) {
            return;
        }

        if (this.block.data.pinOnTheTop) {
            this.pinedTopBlockHeight = (document.querySelector(`[id='block-${this.block.id}']`) as HTMLDivElement)?.offsetHeight;
            this.backgroundColor = websiteColorCss(this.block.styles.backgroundColor as string);

            const htmlNode = document.querySelector('html') as HTMLHtmlElement;
            htmlNode.style.scrollPaddingTop = (this.pinedTopBlockHeight?.toString() ?? '0') + 'px';

            this.changeDetectorRef.detectChanges();
        }

        if (this.block.data.pinOnTheBottom) {
            this.pinedBottomBlockHeight = (document.querySelector(`[id='block-${this.block.id}']`) as HTMLDivElement)?.offsetHeight;
            this.backgroundColor = websiteColorCss(this.block.styles.backgroundColor as string);

            this.changeDetectorRef.detectChanges();
        }

        this.setScrollEvents();

        // TODO: ¯\_(ツ)_/¯
        this.isVisibleConditionBlock$()
            .pipe(
                take(1),
                filter((isVisible) => !isVisible),
            )
            .subscribe(() => {
                this.elementRef.nativeElement.querySelector('.block-container').remove();
            });
    }

    public get blockId(): string {
        return `block-${this.block.id}`;
    }

    private initScrollSubscription(effect: EScrollEffect) {
        document.addEventListener('scroll', () => {
            const clientHeight = document.documentElement.clientHeight;
            const blockOffset = this.blockBg.nativeElement.getBoundingClientRect().top;
            const isParallax = effect === EScrollEffect.PARALLAX;
            const isInViewZone = blockOffset < clientHeight;

            if (isParallax && isInViewZone) {
                this.moveBgParallax();
            } else {
                if (isIphone()) this.moveBgFixed();
            }
        });
    }

    private setScrollEvents(): void {
        const desktopParams = {
            isEffect: this.block.data.isScrollEffect,
            effect: this.block.data.scrollEffect,
        };

        const mobileSource = this.block.data.isSynchronizeStyle ? this.block.data : this.block.mobileData;

        const mobileParams = {
            isEffect: mobileSource?.isScrollEffect,
            effect: mobileSource?.scrollEffect,
        };

        // Determine if current device should use mobile params
        let isMobile = false;
        if (this.isBrowser) {
            const ua = navigator.userAgent || '';
            const hasMatchMedia = typeof window !== 'undefined' && 'matchMedia' in window;
            const isTouch = hasMatchMedia ? window.matchMedia('(pointer: coarse)').matches : false;
            const isSmall = hasMatchMedia ? window.matchMedia('(max-width: 1024px)').matches : false;
            isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) || isTouch || isSmall;
        }

        if (isMobile) {
            this.applyScrollLogic(mobileParams);
        } else {
            this.applyScrollLogic(desktopParams);
        }
    }

    private applyScrollLogic(params: { isEffect: boolean; effect: EScrollEffect }): void {
        if (!params.isEffect) {
            return;
        }

        if (params.effect === EScrollEffect.FIXED && isIphone()) {
            const bgImagePropertyUrl = window
                .getComputedStyle(this.blockBg.nativeElement)
                .getPropertyValue('background-image')
                .match(/url\("([^"]+)"\)/g)?.[0];

            if (bgImagePropertyUrl) {
                const actualImage = new Image();
                actualImage.src = bgImagePropertyUrl.replace('url("', '').replace('")', '');

                actualImage.onload = () => {
                    this.initFixedScroll(this.blockBg, this.renderer, actualImage.height);
                    this.initScrollSubscription(params.effect);
                };

                this.renderer.setStyle(this.blockBg.nativeElement, 'background-attachment', 'scroll');
            }
        }

        if (params.effect === EScrollEffect.PARALLAX) {
            this.moveBgParallax();
            this.initScrollSubscription(params.effect);
        }
    }

    private moveBgParallax() {
        const clientHeight = document.documentElement.clientHeight;
        const blockOffsetTop = this.blockBg.nativeElement.getBoundingClientRect().top;
        const blockOffsetBottom = this.blockBg.nativeElement.getBoundingClientRect().bottom;
        const blockHeight = this.blockBg.nativeElement.getBoundingClientRect().height;

        if (blockOffsetTop > clientHeight || blockOffsetTop + blockHeight < 0) return;

        const percent = ((((blockOffsetBottom + blockOffsetTop - clientHeight) * 80) / blockHeight) * 40) / 100 / 2;

        this.renderer.setStyle(this.parallax.nativeElement, 'transform', `translate3d(0%, ${percent}%, 0px)`);
    }

    private moveBgFixed() {
        const clientHeight = document.documentElement.clientHeight;
        const blockOffset = this.blockBg.nativeElement.getBoundingClientRect().top;
        const blockHeight = this.blockBg.nativeElement.getBoundingClientRect().height;

        if (blockOffset > clientHeight || blockOffset + blockHeight < 0) return;

        const xPosition = (this.imgHeight - clientHeight + blockOffset) * -1;
        const bgPosition = `50% ${xPosition}px`;

        this.renderer.setStyle(this.blockBg.nativeElement, 'background-position', bgPosition);
    }

    private initFixedScroll(element: ElementRef<HTMLDivElement>, renderer: Renderer2, actualImageHeight: number): void {
        this.imgHeight = actualImageHeight;
        this.moveBgFixed();
    }

    public get isParallax(): boolean {
        return this.block?.data?.isScrollEffect && this.block?.data?.scrollEffect === EScrollEffect.PARALLAX;
    }

    public getBackgroundVideoUrl(): string | boolean {
        if (this.block.data?.backgroundVideo === null) {
            return false;
        }

        return this.imageService.formatImageSrc(this.block.data?.backgroundVideo.url);
    }

    public getMobileBackgroundVideoUrl(): string | boolean {
        if (this.block.mobileData?.backgroundVideo === null) {
            return false;
        }

        return this.imageService.formatImageSrc(this.block.mobileData?.backgroundVideo.url);
    }

    public isVisibleConditionBlock$(): Observable<boolean> {
        const context: ConditionContext = {
            userId: this.transferStateService.get('userId'),
            isAuthorizedStudent: this.isBrowser ? this.cookieService.check('oauth_access_token') : false,
            isBrowser: this.isBrowser,
            isServer: this.isServer,
        };

        return this.blockDisplayConditionService.checkVisibility(this.block.blockDisplay, context);
    }
}
